import type { TChapterImagePrompts } from '@/types/images'
import type { TChapter } from '@/types/chat'

const CLOSE_PREFIX = 'Close-up of '
const WIDE_PREFIX = 'Wide view of '
const CLOSE_SUFFIX = ', shallow depth of field, natural light'
const WIDE_SUFFIX = ', more of the setting visible, natural light'

/**
 * Two framings of one keyword string, one per AI image slot. Two draws of a single
 * description come back as the same picture, so the slots only differ when their text
 * does. Used where no model-written scene descriptions are available. The wording names
 * the framing and not the medium, so a style clause stays free to set the medium.
 */
export function imageFramingPair(keywords: string[]): string[] {
  const subject = keywords.join(', ').trim()
  if (!subject) {
    return []
  }
  return [CLOSE_PREFIX + subject + CLOSE_SUFFIX, WIDE_PREFIX + subject + WIDE_SUFFIX]
}

function extractKeywordsArray(val: unknown): string[] | null {
  if (Array.isArray(val)) {
    const strings = val.filter((k): k is string => typeof k === 'string' && k.trim().length > 0)
    return strings.length > 0 ? strings : null
  }
  if (val && typeof val === 'object') {
    const obj = val as Record<string, unknown>
    const candidate = obj.keywords ?? obj.keyword ?? obj.terms ?? Object.values(obj)[0]
    if (Array.isArray(candidate)) {
      return extractKeywordsArray(candidate)
    }
  }
  return null
}

function extractDescriptions(val: unknown): string[] {
  if (!val || typeof val !== 'object' || Array.isArray(val)) {
    return []
  }
  const obj = val as Record<string, unknown>
  const descriptions = [obj.close, obj.wide]
    .filter((d): d is string => typeof d === 'string' && d.trim().length > 0)
    .map((d) => d.trim())
  return descriptions.length === 2 ? descriptions : []
}

function extractChapterPrompts(val: unknown): TChapterImagePrompts | null {
  const keywords = extractKeywordsArray(val)
  if (keywords === null) {
    return null
  }
  const descriptions = extractDescriptions(val)
  return { keywords, descriptions: descriptions.length === 2 ? descriptions : imageFramingPair(keywords) }
}

/**
 * The small model is inconsistent about the shape it returns, so accept an array of
 * objects, an array of bare keyword arrays, and an object keyed by chapter name. A
 * chapter that carries no usable `close`/`wide` pair falls back to the static framings.
 */
export function parseImagePromptsResponse(text: string, chapterCount: number): TChapterImagePrompts[] | null {
  const cleaned = text
    .trim()
    .replace(/```json\n?|\n?```/g, '')
    .trim()
  if (!cleaned) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(cleaned)
  } catch {
    // Truncated JSON - try to salvage by closing brackets
    const repaired = cleaned.replace(/,\s*$/, '') + (cleaned.includes('[[') ? ']]' : ']')
    try {
      parsed = JSON.parse(repaired)
    } catch {
      return null
    }
  }

  if (Array.isArray(parsed)) {
    const valid = parsed
      .map((item) => extractChapterPrompts(item))
      .filter((r): r is TChapterImagePrompts => r !== null)
    if (valid.length >= chapterCount) {
      return valid.slice(0, chapterCount)
    }
    if (valid.length > 0) {
      return valid
    }
    return null
  }

  if (parsed && typeof parsed === 'object') {
    const valid = Object.values(parsed as Record<string, unknown>)
      .map((item) => extractChapterPrompts(item))
      .filter((r): r is TChapterImagePrompts => r !== null)
    if (valid.length > 0) {
      return valid.slice(0, chapterCount)
    }
  }

  return null
}

const KEYWORDS_INSTRUCTION = `keywords: 2 English stock-photo search terms for Unsplash, naming what the chapter is about. The two are joined into one search and Unsplash returns only photos matching every word, so keep each one an ordinary subject a stock library would tag, one or two words, and pick two that can appear in the same photograph. Good: "morning routine", "messy desk", "mountain path", "open road", "handshake". Bad: "concept", "introduction", "chapter one" - nothing to photograph. Bad: "hand reaching water", "sunrise kitchen counter" - a described scene is not a search term and matches no photo.`

const KEYWORDS_SEPARATION = `Keep the keywords independent of the two sentences below: they search a photo library, so they stay plain and broad even when the scene is specific.`

const DESCRIPTIONS_HEAD = `close and wide describe the SAME scene two ways, so that two generated images for one chapter never look alike:
- close: the subject in the middle of the action, framed tight, shallow depth of field.
- wide: the place and the objects, with no person or one small distant figure, wide establishing shot, more of the setting visible.

Rules for both descriptions, in English whatever the language of the chapter:
- One sentence naming one subject, the setting and the light. Never list several topics in one description: one description, one scene.`

const DESCRIPTIONS_TAIL = `- Never describe a comparison, a claim, a list or a before-and-after. Those come back as infographics rather than a single scene.
- No named people, brands or logos.`

const SCENE_STANDS_FOR_IDEA = `- Describe a concrete physical scene that could be drawn or photographed. If the chapter is about an abstract idea, do not try to draw the idea itself. Choose an ordinary physical scene that stands for it and describe that scene.
- Fiction and children's chapters are the exception to the realism rule: describe the story's own characters and places directly.
- No text anywhere in the scene: no signs, labels, book pages, screens showing an interface, whiteboards, diagrams or charts. The image generator is forbidden from drawing text and produces garbled marks when asked for it.`

const SCENE_DRAWN_LITERALLY = `- Describe the subject itself. A diagram, a labelled schematic, a screen, a board or the tools and parts of the work are all allowed, and are better here than an everyday scene that merely stands for the idea.
- Words in the image are allowed, but keep them short: a title, a handful of labels, one line on a board. Never a paragraph, a column of entries or a page of small print - the image generator spells short strings correctly and garbles dense ones.`

const EXAMPLES_GENERAL = `Examples:

"Compound Interest"
close: Close-up of weathered hands stacking coins into a tall column on a kitchen table, warm window light, shallow depth of field
wide: Wide view of a modest sunlit kitchen at dawn, a glass jar of coins on the table and a chair pushed back, soft haze

"Learning to Let Go"
close: Close-up of a woman's hands releasing a paper boat onto slow river water, late afternoon light, shallow depth of field
wide: Wide view of a wide calm river at dusk with one small pale shape drifting midstream, low hills on the far bank

"How Recursion Works"
close: Close-up of hand-carved nesting dolls opened in a descending row on a wooden desk, angled lamp light, shallow depth of field
wide: Wide view of two facing mirrors in a dim hallway repeating a lit doorway into the distance

"The Dragon Who Lost His Fire"
close: Close-up of a small green dragon curled on a mossy boulder with faint smoke at its nostrils, dappled forest light, shallow depth of field
wide: Wide view of a misty pine valley at dawn, a cave mouth on the far cliff and a tiny winged silhouette flying toward it`

const EXAMPLES_WORDS_ALLOWED = `Examples:

"How Unification Works"
close: Close-up of a pencil tree diagram of linked terms on graph paper with two nodes circled, angled desk lamp light, shallow depth of field
wide: Wide view of a small study with a sparse node-and-arrow diagram on a whiteboard, daylight from a side window

"Seating the Queen"
close: Close-up of gloved hands lowering a queen cage between two frames of comb, bright midday light, shallow depth of field
wide: Wide view of three painted hives on a mown slope with a smoker on an upturned crate, hazy morning light

"Reading a Stack Trace"
close: Close-up of a laptop screen showing a short indented list of function names, one line highlighted, dim room, shallow depth of field
wide: Wide view of a cluttered desk at night lit by one monitor, notebook and cold coffee beside the keyboard`

function descriptionsInstruction(wordsAllowed: boolean, languageName: string): string {
  const sceneRules = wordsAllowed ? SCENE_DRAWN_LITERALLY : SCENE_STANDS_FOR_IDEA
  const languageRule =
    wordsAllowed && languageName ? `\n- Any words appearing in the image must be written in ${languageName}.` : ''
  const examples = wordsAllowed ? EXAMPLES_WORDS_ALLOWED : EXAMPLES_GENERAL
  return `${DESCRIPTIONS_HEAD}\n${sceneRules}\n${DESCRIPTIONS_TAIL}${languageRule}\n\n${examples}`
}

/**
 * `languageName` is only used when words are allowed in the image: the descriptions themselves
 * are always written in English.
 */
export function buildImagePromptsRequest(
  chapters: TChapter[],
  options: { withDescriptions: boolean; wordsAllowed: boolean; languageName: string },
): string {
  const chaptersData = chapters
    .map((ch, i) => {
      const subTitles = ch.subchapters.map((s) => s.subchapter_title).filter(Boolean)
      return `Chapter ${i + 1}: ${ch.chapter_title}${subTitles.length > 0 ? '\nSubsections: ' + subTitles.join(', ') : ''}`
    })
    .join('\n\n')

  const shape = options.withDescriptions
    ? `{"keywords": ["...","..."], "close": "one sentence", "wide": "one sentence"}`
    : `{"keywords": ["...","..."]}`
  const body = options.withDescriptions
    ? `${KEYWORDS_INSTRUCTION}\n\n${KEYWORDS_SEPARATION}\n\n${descriptionsInstruction(options.wordsAllowed, options.languageName)}`
    : KEYWORDS_INSTRUCTION

  return `For each chapter below, produce image prompts.\n\nReturn ONLY JSON: an array with one object per chapter, in the same order:\n${shape}\n\n${body}\n\n${chaptersData}`
}
